import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";

import { PermissionKey, PermissionScope } from "@fitness-app/data-models";
import { TemplateType } from "@fitness-app/data-models/entities/AutomationTemplate";
import {
  type PlannedTasksSequence,
  type PlannedTasksSequenceMetadata,
} from "@fitness-app/data-models/entities/ProgramAutomation";

import { usePermissionScope } from "~/hooks/usePermission";
import { useUserRole } from "~/hooks/trainer/useUserRole";
import { supabase, useAppSelector } from "~/store/initializeStore";

export interface ScheduledMessagesSequenceWithAuthor extends Omit<PlannedTasksSequence, "metadata"> {
  metadata: PlannedTasksSequenceMetadata | null;
  author: {
    firstName: string;
    lastName: string;
  } | null;
  teamMember: {
    firstName: string;
    lastName: string;
  } | null;
}

export const useScheduledMessagesSequenceCount = () => {
  const { userId } = useUserRole();

  return useQuery({
    queryKey: ["scheduled_messages_sequence_count", userId],
    queryFn: async () => {
      const { count } = await supabase
        .from("planned_tasks_sequence")
        .select("*", { count: "exact", head: true })
        .eq("type", TemplateType.ScheduledMessages)
        .gte("scheduledAt", dayjs().toISOString())
        .eq("addedBy", userId)
        .throwOnError();

      return count ?? 0;
    },
  });
};

export const useScheduledMessagesSequence = () => {
  const [addedByFilter, setAddedByFilter] = useState<string[] | null>(null);
  const assignedTrainers = useAppSelector((state) => state.team.assignedTrainers);

  const { userId } = useUserRole();
  // ALL widzi wszystkie zaplanowane wiadomości i przyjmuje filtr autora, TEAM
  // swoje plus przypisanych trenerów, ASSIGNED tylko własne. Wcześniej po
  // rolach — administracja wpadała w `else` bez żadnego zawężenia, a filtr
  // autora z UI był dla niej po cichu ignorowany.
  const chatWriteScope = usePermissionScope(PermissionKey.CHAT_WRITE);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["scheduled_messages_sequence", addedByFilter, userId, chatWriteScope],
    queryFn: async () => {
      let query = supabase
        .from("planned_tasks_sequence")
        .select("*, author:addedBy(firstName, lastName), teamMember:teamMemberId(firstName, lastName)")
        .eq("type", TemplateType.ScheduledMessages)
        .gte("scheduledAt", dayjs().toISOString());

      if (addedByFilter?.length && chatWriteScope !== PermissionScope.ASSIGNED) {
        query = query.in("addedBy", addedByFilter);
      } else if (chatWriteScope === PermissionScope.TEAM) {
        query = query.in("addedBy", [
          userId,
          ...assignedTrainers.map((trainer) => trainer.trainer.user?.id || null).filter((item) => !!item),
        ]);
      } else if (chatWriteScope === PermissionScope.ASSIGNED) {
        query = query.in("addedBy", [userId]);
      }

      const { data, error } = await query
        .order("scheduledAt", { ascending: false })
        .limit(100)
        .returns<ScheduledMessagesSequenceWithAuthor[]>();

      if (error) {
        throw new Error(error.message);
      }

      return data ?? [];
    },
  });

  return {
    data: data ?? [],
    isLoading,
    refetch,
    addedByFilter,
    setAddedByFilter,
  };
};
